import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, Capabilities } from '@/api/client'
import { usePageLoader } from '@/components/LogoLoader'
import { StatTile } from '@/components/StatTile'
import { useMe } from '@/features/auth/useAuth'
import { useIsPhone } from '@/lib/useMediaQuery'
import { usePersistedState } from '@/lib/usePersistedState'
import { fmtDateTime, useT } from '@/i18n'
import { inventoryMessages } from '@/i18n/messages/inventory'
import { LabelsDialog } from './LabelsPage'
import { LocationsDialog } from './LocationsDialog'
import { ScanDialog } from './ScanDialog'
import { Select } from '@/components/Select'
import { GhostButton, INPUT, LeftBar, PrimaryButton, StatusBadge, whereText } from './shared'
import type { Drum, DrumStatus, Location } from './types'

const STATUSES: DrumStatus[] = ['in_stock', 'at_site', 'with_crew', 'unregistered', 'written_off']

/**
 * The drum inventory: where every drum is, who has it and how much is on it. Every number is
 * read from the movement ledger (docs/inventory-design.md, phase 2).
 */
export function InventoryPage() {
  const t = useT(inventoryMessages)
  const navigate = useNavigate()
  const phone = useIsPhone()
  const { data: me } = useMe()
  const canMove = me?.capabilities.includes(Capabilities.MoveInventory) ?? false
  const [location, setLocation] = usePersistedState<string>('inv:location', '')
  const [status, setStatus] = usePersistedState<DrumStatus | ''>('inv:status', '')
  const [q, setQ] = useState('')
  const [dialog, setDialog] = useState<'scan' | 'labels' | 'locations' | null>(null)

  const drums = useQuery<Drum[]>({ queryKey: ['inv-drums'], queryFn: () => api.get<Drum[]>('/api/inventory/drums') })
  const locations = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: () => api.get<Location[]>('/api/inventory/locations') })

  const all = drums.data ?? []
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return all.filter((d) =>
      (!location || d.locationId === location) && (!status || d.status === status) &&
      (!needle || [d.code, d.cableName, d.kolonaName, d.projectName].some((v) => v?.toLowerCase().includes(needle))))
  }, [all, location, status, q])

  const live = all.filter((d) => d.status === 'in_stock' || d.status === 'at_site' || d.status === 'with_crew')
  const loader = usePageLoader(drums.isPending, t('loading'))
  if (loader) return loader

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('title')}</h1>
          <p className="mt-1 text-sm text-neutral-500">{t('subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <PrimaryButton onClick={() => setDialog('scan')}>⌖ {t('scan')}</PrimaryButton>
          {canMove && <GhostButton onClick={() => setDialog('labels')}>{t('printLabels')}</GhostButton>}
          <GhostButton onClick={() => setDialog('locations')}>{t('locations')}</GhostButton>
          <GhostButton onClick={() => navigate('/inventory/reconciliation')}>{t('reconOpen')}</GhostButton>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        <StatTile label={t('tileStock')} value={live.filter((d) => d.status === 'in_stock').length} unit="" />
        <StatTile label={t('tileSite')} value={live.filter((d) => d.status === 'at_site').length} unit="" />
        <StatTile label={t('tileCrew')} value={live.filter((d) => d.status === 'with_crew').length} unit="" />
        <StatTile label={t('tileMetres')} value={live.reduce((s, d) => s + (d.remainingM ?? 0), 0)} unit="m" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('searchPlaceholder')}
          className={`${INPUT} basis-full sm:basis-72`} aria-label={t('searchPlaceholder')} />
        <Select value={location} onChange={setLocation} className="w-fit min-w-[10rem]"
          empty={{ value: '', label: `${t('colWhere')}: ${t('filterAll')}` }}
          options={(locations.data ?? []).map((l) => ({ value: l.id, label: l.name }))} />
        <div className="flex flex-wrap gap-1">
          {(['', ...STATUSES] as const).map((s) => (
            <button key={s || 'all'} type="button" onClick={() => setStatus(s)}
              className={`min-h-9 rounded-full px-3 text-xs font-medium ring-1 ${status === s
                ? 'bg-neutral-900 text-white ring-neutral-900 dark:bg-white dark:text-neutral-900'
                : 'text-neutral-600 ring-neutral-300 hover:bg-neutral-50 dark:text-neutral-300 dark:ring-neutral-700 dark:hover:bg-neutral-800'}`}>
              {s === '' ? t('filterAll') : <StatusLabel status={s} />}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="rounded-2xl bg-neutral-50 p-6 text-center text-sm text-neutral-500 dark:bg-neutral-900">{t('noDrums')}</p>
      ) : phone ? (
        <ul className="space-y-2">
          {rows.map((d) => (
            <li key={d.id}>
              <Link to={`/d/${d.code}`} className="block rounded-2xl bg-white p-3 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
                <div className="flex items-center justify-between gap-2">
                  <span className="tabular font-mono text-sm font-semibold">{d.code}</span>
                  <StatusBadge status={d.status} />
                </div>
                <div className="mt-1 truncate text-sm">{d.cableName ?? '—'}</div>
                <div className="mt-0.5 truncate text-xs text-neutral-500">{whereText(d)}{d.projectName ? ` · ${d.projectName}` : ''}</div>
                <div className="mt-2"><LeftBar drum={d} wide /></div>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <div className="overflow-x-auto rounded-2xl bg-white ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 text-left text-xs text-neutral-500 dark:border-neutral-800">
                <th className="px-3 py-2 font-medium">{t('colCode')}</th>
                <th className="px-3 py-2 font-medium">{t('colCable')}</th>
                <th className="px-3 py-2 font-medium">{t('colWhere')}</th>
                <th className="px-3 py-2 font-medium">{t('colProject')}</th>
                <th className="px-3 py-2 font-medium">{t('colLeft')}</th>
                <th className="px-3 py-2 font-medium">{t('colLast')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} onClick={() => navigate(`/d/${d.code}`)}
                  className="cursor-pointer border-b border-neutral-100 last:border-0 hover:bg-neutral-50 dark:border-neutral-800/60 dark:hover:bg-neutral-800/40">
                  <td className="px-3 py-2"><span className="font-mono font-semibold">{d.code}</span></td>
                  <td className="px-3 py-2">
                    <div>{d.cableName ?? <StatusBadge status={d.status} />}</div>
                    {d.provider && <div className="text-xs text-neutral-500">{d.provider}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col gap-1">{d.cableName && <StatusBadge status={d.status} />}<span className="text-xs text-neutral-600 dark:text-neutral-300">{whereText(d)}</span></div>
                  </td>
                  <td className="px-3 py-2 text-xs">{d.projectName ?? '—'}</td>
                  <td className="px-3 py-2"><LeftBar drum={d} /></td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-neutral-500">{d.lastAt ? fmtDateTime(d.lastAt) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dialog === 'scan' && <ScanDialog onClose={() => setDialog(null)} />}
      {dialog === 'labels' && <LabelsDialog onClose={() => setDialog(null)} />}
      {dialog === 'locations' && <LocationsDialog canEdit={canMove} onClose={() => setDialog(null)} />}
    </div>
  )
}

function StatusLabel({ status }: { status: DrumStatus }) {
  const t = useT(inventoryMessages)
  return <>{{ unregistered: t('statusUnregistered'), in_stock: t('statusInStock'), at_site: t('statusAtSite'),
    with_crew: t('statusWithCrew'), written_off: t('statusWrittenOff') }[status]}</>
}
